import { RotateCcw } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchInput } from '@/components/ui/search-input';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import type { SupportedTimeZone } from '@/constants/date';
import { dateToNaiveDateTimeString } from '@/utils/date';

import {
  DELIVERY_EVENT_TYPE_OPTIONS,
  DELIVERY_SEARCH_OPTIONS,
  DELIVERY_STATUS_OPTIONS,
  type DeliveryFilterState,
  type DeliverySearchType,
} from '../utils';

interface DeliveryFiltersProps {
  filters: DeliveryFilterState;
  onChange: (next: DeliveryFilterState) => void;
  /** Omitted on an endpoint's own page, where the endpoint is fixed. */
  endpointOptions?: { label: string; value: string }[];
  timeZone: SupportedTimeZone;
  showReset: boolean;
  onReset: () => void;
}

/** Search by caseId / referenceId, then endpoint, status, event type and time range. */
export function DeliveryFilters({
  filters,
  onChange,
  endpointOptions,
  timeZone,
  showReset,
  onReset,
}: DeliveryFiltersProps) {
  const set = (patch: Partial<DeliveryFilterState>) => onChange({ ...filters, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-3">
      <SearchInput
        searchOptions={DELIVERY_SEARCH_OPTIONS}
        activeSearchType={filters.searchType}
        searchValues={{ [filters.searchType]: filters.searchValue }}
        onSearchValueChange={(type, value) =>
          set({ searchType: type as DeliverySearchType, searchValue: value })
        }
        onSearchTypeChange={type => set({ searchType: type as DeliverySearchType })}
        placeholder={type => (type === 'case_id' ? 'Exact case ID' : 'Exact reference ID')}
        className="w-[340px]"
        searchTypeTriggerClassName="w-max min-w-max shrink-0 [&>span]:flex-none"
        minWidth="160px"
      />

      {endpointOptions && (
        <SearchableMultiSelect
          options={endpointOptions}
          selected={filters.endpointIds}
          onChange={value => set({ endpointIds: value })}
          placeholder="Endpoint"
          className="w-[175px] px-3"
          contentClassName="w-[18rem]"
          searchPlaceholder="Search endpoints..."
          emptyMessage="No endpoints found."
          maxCount={1}
        />
      )}

      <SearchableMultiSelect
        options={DELIVERY_STATUS_OPTIONS}
        selected={filters.statuses}
        onChange={value => set({ statuses: value })}
        placeholder="Status"
        className="w-[150px] px-3"
        contentClassName="w-auto min-w-[180px]"
        searchPlaceholder="Search statuses..."
        emptyMessage="No statuses found."
        maxCount={1}
      />

      <SearchableMultiSelect
        options={DELIVERY_EVENT_TYPE_OPTIONS}
        selected={filters.eventTypes}
        onChange={value => set({ eventTypes: value })}
        placeholder="Event type"
        className="w-[160px] px-3"
        contentClassName="w-auto min-w-[220px]"
        searchPlaceholder="Search event types..."
        emptyMessage="No event types found."
        maxCount={1}
      />

      <DateRangePicker
        // Naive strings, not Dates: a stable prop keeps the picker from resetting mid-edit.
        initialDateFrom={filters.dateFrom || undefined}
        initialDateTo={filters.dateTo || undefined}
        timezone={timeZone}
        showCompare={false}
        showTime
        placeholder="Any time"
        onUpdate={({ range }) => {
          if (!range.from) return;
          set({
            dateFrom: dateToNaiveDateTimeString(range.from),
            dateTo: dateToNaiveDateTimeString(range.to || range.from),
          });
        }}
      />

      {showReset && (
        <SimpleTooltipV2 content="Reset filters" side="top">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10"
            onClick={onReset}
            aria-label="Reset filters"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </SimpleTooltipV2>
      )}
    </div>
  );
}
